import { msg } from "@omegagrid/localize";
import { NodeSource, TreeAdapter, TreeOptions } from "@omegagrid/tree";
import rp from '@reporting/rp-app-host';
import { Follower } from "../types";

/** A follower dropdown picks several people; the group headings carry no checkbox and no level button (the "1" would pick them). */
export const FOLLOWER_LIST_OPTIONS: Partial<TreeOptions> = {
	selectMode: 'multiple', autoClose: false, search: true, globalLevels: false,
};

/** The followers of a request as the server stores them (WHO_FOLLOWER): user names separated by semicolons. */
export function splitFollowers(value: string | null | undefined): string[] {
	return [...new Set((value ?? '').split(';').map(s => s.trim()).filter(Boolean))];
}

/**
 * The followers as the server takes them, from the value of a follower control or cell: the keys picked, or the
 * [key, ...] tuples the tree serializes (a stored list passes through); null for nobody.
 */
export function joinFollowers(value: unknown): string | null {
	if (value == null || value === '') return null;
	const items = Array.isArray(value) ? value : typeof value === 'string' ? value.split(';') : [value];
	const keys = items.map(item => Array.isArray(item) ? item[0] : item).map(k => String(k ?? '').trim()).filter(Boolean);
	return keys.length ? [...new Set(keys)].join(';') : null;
}

/** The value of a multi-select list control for a stored list: the [key, 0, 1] tuples the forms use (see filterFormAdapter). */
export function followerListValue(value: string | null | undefined): [string, number, number][] | null {
	const keys = splitFollowers(value);
	return keys.length ? keys.map(k => [k, 0, 1]) : null;
}

/** The followers of a stored list by name, for a system message: the people of the dropdown; an unknown one keeps his user name. */
export function followerNames(value: string | null | undefined, list: { k: number | string, v: string }[]): string {
	return splitFollowers(value).map(k => list.find(item => String(item.k) === k)?.v ?? k).join(', ');
}

/**
 * The followers a request has, by name - the people to keep on its dropdown (followerNodes `keep`) when the server does
 * not return them, they lost the right since. who_follower_dv lists their logins in the order of the stored list.
 */
export function storedFollowers(ticket: { who_follower?: string, who_follower_dv?: string }): { k: string, v: string }[] {
	const keys = splitFollowers(ticket.who_follower);
	const names = (ticket.who_follower_dv ?? '').split(', ');
	return keys.map((k, i) => ({ k, v: names.length === keys.length ? names[i] : k }));
}

/**
 * The people the followers of a request of the company can be picked from: those with access to its helpdesk and, for an
 * SA, the SA users (rp-ms-helpdesk tickets/followers).
 */
export async function loadFollowers(accounting: number): Promise<Follower[]> {
	return await rp.ms('helpdesk').get<Follower[]>('tickets/followers', { accounting }) ?? [];
}

/**
 * The items of a follower dropdown: the SA users and the users of the company in two groups when there are SA users to
 * show (an SA gets them, a customer does not - his list needs no heading). `keep` are people to show although the server
 * did not return them - the followers a request has who lost the right since; they join the users, first.
 */
export function followerNodes(followers: Follower[], keep: { k: string, v: string }[] = []): NodeSource[] {
	const person = (f: Follower): NodeSource => ({ k: f.user_name, v: f.login_name });
	const sa = followers.filter(f => f.sa).map(person);
	const users = followers.filter(f => !f.sa).map(person);
	users.unshift(...keep.filter(k => !followers.some(f => f.user_name === k.k)).map(k => ({ ...k })));
	if (!sa.length) return users;
	const group = (key: string, label: string, icon: string, items: NodeSource[]): NodeSource[] =>
		items.length ? [{ k: key, v: label, l: 0, ch: false, icon }, ...items.map(item => ({ ...item, l: 1 }))] : [];
	return [...group('g-sa', `SA ${msg('users')}`, 'user-crown', sa), ...group('g-users', msg('Users'), 'user', users)];
}

/**
 * The follower dropdown of the new-request form: loaded on every open for the company picked in the form, so it follows
 * a change of the company.
 */
export class FollowerTreeAdapter extends TreeAdapter {

	constructor(
		private accounting: () => number
	) { super() }

	async getTree() {
		return followerNodes(await loadFollowers(this.accounting()));
	}

	getOptions(): Partial<TreeOptions> {
		return { ...FOLLOWER_LIST_OPTIONS, alwaysRefresh: true };
	}

}
